import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import io from 'socket.io-client';
import mitt from 'mitt';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from './AuthContext'; // Assuming AuthContext provides useAuth hook
import { validateIncomingEvent, validateOutgoingEvent, allIncomingEventSchemas } from '../schemas/socketEvents'; // CORRECTED PATH
import { isPublicRoute } from '../components/utils';
import { clearClientAuthState } from '../utils/authCleanup';
import { v4 as uuidv4 } from 'uuid';
import { emitFrontendDiagnostic } from '../utils/diagnostics';

// Create an emitter instance (can be outside the component if preferred)
// REMOVED: const emitter = mitt();
// REMOVED: export const testEmitter = emitter;

// Export the emitter for testing/debugging purposes FROM SocketContext.jsx
// This allows other parts of the app (like a test button in MatchPage)
// to simulate server-sent events for development.
export const debugEmitter = mitt(); // Renamed from 'emitter' to be clear it's for debug

// 1. Define the Context
export const SocketContext = createContext(null);

// 2. Create a custom hook for easy access
export const useSocket = () => {
  return useContext(SocketContext);
};

function getTokenFingerprint(token) {
  if (!token || typeof token !== 'string') {
    return null;
  }

  let hash = 2166136261;
  for (let i = 0; i < token.length; i += 1) {
    hash ^= token.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return `${token.length}.${(hash >>> 0).toString(36)}`;
}

function updateSocketAuthToken(socket, token) {
  if (!socket || !token) {
    return false;
  }

  socket.auth = {
    ...(socket.auth || {}),
    token
  };

  return true;
}

function redirectToLoginIfProtectedRoute() {
  if (typeof window === 'undefined') return;
  if (!isPublicRoute(window.location.pathname)) {
    window.location.href = '/login';
  }
}

async function clearRejectedSocketAuthState() {
  try { await supabase.auth.signOut(); } catch (_) {}
  clearClientAuthState();
}

async function isRejectedSocketAuthConfirmed(accessToken) {
  const apiBaseUrl = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '');

  try {
    const response = await fetch(`${apiBaseUrl}/auth/profile`, {
      method: 'GET',
      credentials: 'include',
      headers: accessToken
        ? { Authorization: `Bearer ${accessToken}` }
        : undefined
    });
    return response.status === 401 || response.status === 404;
  } catch (error) {
    console.warn('SocketProvider: Could not verify the rejected socket session with the backend.', error);
    return false;
  }
}

async function clearRejectedSocketAuthStateIfConfirmed(accessToken) {
  const rejectionConfirmed = await isRejectedSocketAuthConfirmed(accessToken);
  if (!rejectionConfirmed) {
    console.warn('SocketProvider: Backend profile check did not confirm an invalid session. Keeping auth state.');
    return false;
  }

  await clearRejectedSocketAuthState();
  redirectToLoginIfProtectedRoute();
  return true;
}

const SOCKET_AUTH_FAILURE_CODES = {
  NO_TOKEN: 'NO_TOKEN',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  INVALID_TOKEN: 'INVALID_TOKEN',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  LOCAL_USER_NOT_FOUND: 'LOCAL_USER_NOT_FOUND'
};

function getSocketAuthFailureCode(error, fallbackCode = null) {
  return error?.data?.code || error?.data?.reason || error?.code || fallbackCode;
}

function shouldClearRejectedSocketAuth(code) {
  return Object.values(SOCKET_AUTH_FAILURE_CODES).includes(code);
}

function stopSocketReconnection(socket) {
  if (!socket) return;
  try {
    if (socket.io?.opts) {
      socket.io.opts.reconnection = false;
    }
    if (typeof socket.io?.reconnection === 'function') {
      socket.io.reconnection(false);
    }
  } catch (_) {
    // Socket.IO client internals differ by version; this shutdown is best-effort.
  }
}

function startSocketReconnection(socket) {
  if (!socket) return;
  try {
    if (socket.io?.opts) {
      socket.io.opts.reconnection = true;
    }
    if (typeof socket.io?.reconnection === 'function') {
      socket.io.reconnection(true);
    }
  } catch (_) {
    // Socket.IO client internals differ by version; this restart is best-effort.
  }
}

const SOCKET_CLIENT_INSTANCE_ID_STORAGE_KEY = 'nova.socket.clientInstanceId';
const SOCKET_CLIENT_INSTANCE_RELEASE_STORAGE_KEY = 'nova.socket.clientInstanceId.release';
const SOCKET_CLIENT_INSTANCE_OWNER_PREFIX = 'nova.socket.clientInstanceId.owner.';
const SOCKET_CLIENT_INSTANCE_OWNER_TTL_MS = 5 * 60 * 1000;
const SOCKET_CLIENT_INSTANCE_OWNER_HEARTBEAT_MS = 30 * 1000;
const SOCKET_CLIENT_INSTANCE_RELEASE_GRACE_MS = 10 * 1000;
const SOCKET_CLIENT_INSTANCE_TAB_ID = uuidv4();
const SOCKET_CONNECT_TIMEOUT_MS = 20 * 1000;
const SOCKET_RECONNECTION_DELAY_MS = 1000;
const SOCKET_RECONNECTION_DELAY_MAX_MS = 5000;
const SOCKET_RECONNECTION_ATTEMPTS = 30;
const SOCKET_USER_NOT_FOUND_RETRY_DELAYS_MS = [1000, 2000, 3000, 4000];

function getSocketClientInstanceOwnerKey(clientInstanceId) {
  return `${SOCKET_CLIENT_INSTANCE_OWNER_PREFIX}${clientInstanceId}`;
}

function isReloadNavigation() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const [navigationEntry] = window.performance?.getEntriesByType?.('navigation') || [];
    return navigationEntry?.type === 'reload';
  } catch (_) {
    return false;
  }
}

function readSocketClientInstanceOwner(clientInstanceId) {
  if (typeof window === 'undefined' || !clientInstanceId) {
    return null;
  }

  try {
    const rawOwner = window.localStorage?.getItem(getSocketClientInstanceOwnerKey(clientInstanceId));
    if (!rawOwner) {
      return null;
    }

    const owner = JSON.parse(rawOwner);
    return owner && typeof owner === 'object' ? owner : null;
  } catch (_) {
    return null;
  }
}

function writeSocketClientInstanceOwner(clientInstanceId) {
  if (typeof window === 'undefined' || !clientInstanceId) {
    return;
  }

  try {
    window.localStorage?.setItem(
      getSocketClientInstanceOwnerKey(clientInstanceId),
      JSON.stringify({
        tabId: SOCKET_CLIENT_INSTANCE_TAB_ID,
        updatedAt: Date.now()
      })
    );
  } catch (_) {
    // Browser storage can be unavailable in private mode or tests.
  }
}

function writeSocketClientInstanceReleaseMarker(clientInstanceId) {
  if (typeof window === 'undefined' || !clientInstanceId) {
    return;
  }

  try {
    window.sessionStorage?.setItem(
      SOCKET_CLIENT_INSTANCE_RELEASE_STORAGE_KEY,
      JSON.stringify({
        clientInstanceId,
        releasedAt: Date.now()
      })
    );
  } catch (_) {
    // Browser storage can be unavailable in private mode or tests.
  }
}

function consumeFreshSocketClientInstanceReleaseMarker(clientInstanceId) {
  if (typeof window === 'undefined' || !clientInstanceId) {
    return false;
  }

  try {
    const rawRelease = window.sessionStorage?.getItem(SOCKET_CLIENT_INSTANCE_RELEASE_STORAGE_KEY);
    if (!rawRelease) {
      return false;
    }

    window.sessionStorage?.removeItem(SOCKET_CLIENT_INSTANCE_RELEASE_STORAGE_KEY);
    const release = JSON.parse(rawRelease);
    const releasedAt = Number(release?.releasedAt);
    return release?.clientInstanceId === clientInstanceId &&
      Number.isFinite(releasedAt) &&
      Date.now() - releasedAt < SOCKET_CLIENT_INSTANCE_RELEASE_GRACE_MS;
  } catch (_) {
    return false;
  }
}

function releaseSocketClientInstanceOwner(clientInstanceId) {
  if (typeof window === 'undefined' || !clientInstanceId) {
    return;
  }

  try {
    writeSocketClientInstanceReleaseMarker(clientInstanceId);
    const owner = readSocketClientInstanceOwner(clientInstanceId);
    if (owner?.tabId === SOCKET_CLIENT_INSTANCE_TAB_ID) {
      window.localStorage?.removeItem(getSocketClientInstanceOwnerKey(clientInstanceId));
    }
  } catch (_) {
    // Browser storage can be unavailable in private mode or tests.
  }
}

function hasFreshDifferentSocketClientInstanceOwner(owner) {
  if (!owner?.tabId || owner.tabId === SOCKET_CLIENT_INSTANCE_TAB_ID) {
    return false;
  }

  const updatedAt = Number(owner.updatedAt);
  return Number.isFinite(updatedAt) &&
    Date.now() - updatedAt < SOCKET_CLIENT_INSTANCE_OWNER_TTL_MS;
}

function claimSocketClientInstanceId(clientInstanceId) {
  if (typeof window === 'undefined' || !clientInstanceId) {
    return clientInstanceId || uuidv4();
  }

  let claimedClientInstanceId = clientInstanceId;
  try {
    const owner = readSocketClientInstanceOwner(claimedClientInstanceId);
    consumeFreshSocketClientInstanceReleaseMarker(claimedClientInstanceId);
    if (
      hasFreshDifferentSocketClientInstanceOwner(owner) &&
      !isReloadNavigation()
    ) {
      claimedClientInstanceId = uuidv4();
      window.sessionStorage?.setItem(
        SOCKET_CLIENT_INSTANCE_ID_STORAGE_KEY,
        claimedClientInstanceId
      );
    }

    writeSocketClientInstanceOwner(claimedClientInstanceId);
    return claimedClientInstanceId;
  } catch (_) {
    return claimedClientInstanceId;
  }
}

function getStableSocketClientInstanceId() {
  if (typeof window === 'undefined') {
    return uuidv4();
  }

  try {
    const existingClientInstanceId = window.sessionStorage?.getItem(SOCKET_CLIENT_INSTANCE_ID_STORAGE_KEY);
    if (existingClientInstanceId && existingClientInstanceId.trim()) {
      return claimSocketClientInstanceId(existingClientInstanceId);
    }

    const nextClientInstanceId = uuidv4();
    window.sessionStorage?.setItem(SOCKET_CLIENT_INSTANCE_ID_STORAGE_KEY, nextClientInstanceId);
    return claimSocketClientInstanceId(nextClientInstanceId);
  } catch (_) {
    return uuidv4();
  }
}

function getOutgoingSocketPayloadSummary(eventType, payload = {}) {
  const commandData = payload?.command_data;
  return {
    eventType,
    payloadType: payload === null ? 'null' : typeof payload,
    payloadKeyCount: payload && typeof payload === 'object' ? Object.keys(payload).length : 0,
    commandType: payload?.command_type,
    commandDataKeyCount: commandData && typeof commandData === 'object' ? Object.keys(commandData).length : 0,
    matchIdPresent: Boolean(payload?.matchId || commandData?.matchId || commandData?.match_id),
    teamSidePresent: Boolean(commandData?.team_id || commandData?.teamId),
    clientAttemptSupplied: Boolean(
      payload?.engineBootAttemptId ||
      payload?.engine_boot_attempt_id ||
      commandData?.engineBootAttemptId ||
      commandData?.engine_boot_attempt_id
    )
  };
}

// 3. Create the Provider Component
export const SocketProvider = ({ children, socketFactory = io }) => {
  const { session, user: authUser } = useAuth();
  // -----------------------------
  // Stable state refs (Option A)
  // -----------------------------
  // Keep React state for occasional provider re-renders/logging, but move
  // frequently-read values to refs so the context object can stay stable
  // without relying on state changes.
  const [status, setStatus] = useState('disconnected');

  // Ref that always holds the latest connection status. This is what the
  // context getter will expose so consumers read fresh values without a new
  // context object (and therefore no extra React renders).
  const statusRef = useRef('disconnected');

  // Whenever the React state changes, keep the ref in sync. This keeps all
  // existing setStatus calls working while ensuring the ref is always up to
  // date for the stable context getter.
  useEffect(() => {
    statusRef.current = status;
  }, [status]);
  const [socketId, setSocketId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [gameState, setGameState] = useState({ commentary: [] });
  const [authRetryCount, setAuthRetryCount] = useState(0); // Track auth retry attempts
  const [reconnectNonce, setReconnectNonce] = useState(0);
  const socketInstanceRef = useRef(null);
  const socketAuthRepairRef = useRef({
    connectionKey: null,
    refreshAttempts: 0,
    userNotFoundAttempts: 0,
    inFlight: false
  });
  const socketAuthOverrideTokenRef = useRef(null);
  const pendingLeaveMatchIdsRef = useRef(new Set());
  const matchStreamClientSeqRef = useRef(0);
  const clientInstanceIdRef = useRef(null);
  const diagnosticAuthRef = useRef({
    expectedSupabaseUserId: null,
    expiresAt: null
  });
  // const emitter = mitt(); // Use the exported debugEmitter instead

  const handleAnyEventRef = useRef(null);

  if (!clientInstanceIdRef.current) {
    clientInstanceIdRef.current = getStableSocketClientInstanceId();
  }

  useEffect(() => {
    const clientInstanceId = clientInstanceIdRef.current;
    if (!clientInstanceId || typeof window === 'undefined') {
      return undefined;
    }

    writeSocketClientInstanceOwner(clientInstanceId);
    const heartbeatId = window.setInterval(() => {
      writeSocketClientInstanceOwner(clientInstanceId);
    }, SOCKET_CLIENT_INSTANCE_OWNER_HEARTBEAT_MS);
    const releaseClaim = () => {
      releaseSocketClientInstanceOwner(clientInstanceId);
    };

    window.addEventListener('pagehide', releaseClaim);
    window.addEventListener('beforeunload', releaseClaim);

    return () => {
      window.clearInterval(heartbeatId);
      window.removeEventListener('pagehide', releaseClaim);
      window.removeEventListener('beforeunload', releaseClaim);
      releaseClaim();
    };
  }, []);

  const sessionUserId = session?.user?.id;
  const authUserSupabaseId = authUser?.supabaseId;
  const authUserId = authUser?.id;
  const authUserMongoId = authUser?._id;
  const accessToken = session?.access_token;
  const expectedSupabaseUserId = sessionUserId || authUserSupabaseId;
  const resolvedNovaUserId = authUserSupabaseId || authUserId || authUserMongoId;

  const getFrontendDiagnosticContext = useCallback((extra = {}) => {
    const expiresAtSec = Number(diagnosticAuthRef.current.expiresAt);
    const tokenExpiresInSec = Number.isFinite(expiresAtSec)
      ? Math.floor(expiresAtSec - Date.now() / 1000)
      : null;

    return {
      socketId: socketInstanceRef.current?.id || null,
      clientInstanceId: clientInstanceIdRef.current || null,
      socketStatus: statusRef.current,
      supabaseUserId: diagnosticAuthRef.current.expectedSupabaseUserId || null,
      tokenExpiresInSec,
      ...extra
    };
  }, []);

  useEffect(() => {
    diagnosticAuthRef.current = {
      expectedSupabaseUserId: expectedSupabaseUserId || null,
      expiresAt: session?.expires_at || null
    };
  }, [expectedSupabaseUserId, session?.expires_at]);

  // Create a stable connection key to prevent unnecessary reconnections
  const connectionKey = useMemo(() => {
    if (!resolvedNovaUserId || !accessToken) return null;
    return `${resolvedNovaUserId}.${getTokenFingerprint(accessToken)}`;
  }, [
    resolvedNovaUserId,
    accessToken
  ]);
  
  const lastConnectionKeyRef = useRef(null);
  const currentConnectionKeyRef = useRef(null);
  currentConnectionKeyRef.current = connectionKey;

  const getLatestAccessToken = useCallback(async () => {
    if (socketAuthOverrideTokenRef.current) {
      return socketAuthOverrideTokenRef.current;
    }

    const fallbackToken = session?.access_token || null;

    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.warn('SocketProvider: Could not read latest Supabase session before socket connect.', error);
        return fallbackToken;
      }

      const latestSession = data?.session || null;
      if (!latestSession) {
        return null;
      }

      const latestSessionUserId = latestSession?.user?.id;
      if (
        expectedSupabaseUserId &&
        latestSessionUserId &&
        latestSessionUserId !== expectedSupabaseUserId
      ) {
        console.warn('SocketProvider: Latest Supabase session belongs to a different user. Aborting socket token use.');
        return null;
      }

      return latestSession.access_token || null;
    } catch (error) {
      console.warn('SocketProvider: Exception while reading latest Supabase session before socket connect.', error);
      return fallbackToken;
    }
  }, [expectedSupabaseUserId, session?.access_token]);

  useEffect(() => {
    socketAuthOverrideTokenRef.current = null;
    updateSocketAuthToken(socketInstanceRef.current, session?.access_token);
  }, [session?.access_token]);

  // Precompute the set of event types that have explicit Zod schemas so we can
  // skip validation (and avoid runtime errors) for unknown events such as
  // dynamically-generated *\_ack_* messages coming from the engine.
  const knownIncomingEventTypes = useMemo(() => {
    try {
      // allIncomingEventSchemas.options is an array of object schemas that each
      // have a `.shape.type` Zod literal. Extract the literal value.
      return new Set(allIncomingEventSchemas.options.map(opt => opt.shape.type.value));
    } catch (err) {
      console.error('SocketProvider: Failed to derive knownIncomingEventTypes', err);
      return new Set();
    }
  }, []);
  
  // Reset auth retry count when user.id changes (login/logout)
  useEffect(() => {
    if (lastConnectionKeyRef.current !== connectionKey) {
      socketAuthRepairRef.current = {
        connectionKey,
        refreshAttempts: 0,
        userNotFoundAttempts: 0,
        inFlight: false
      };
      socketAuthOverrideTokenRef.current = null;
      if (authRetryCount > 0) {
        console.log('SocketProvider: User changed, resetting auth retry count.');
        setAuthRetryCount(0);
      }
    }
  }, [connectionKey, authRetryCount]);

  useEffect(() => {
    if (!session?.user?.id) {
      pendingLeaveMatchIdsRef.current.clear();
    }
  }, [session?.user?.id]);

  useEffect(() => {
    const emitPageDiagnostic = (eventType, extra = {}, options = {}) => {
      emitFrontendDiagnostic(
        eventType,
        getFrontendDiagnosticContext(extra),
        options
      );
    };

    const handleVisibilityChange = () => emitPageDiagnostic('page_visibility_change');
    const handleOnline = () => emitPageDiagnostic('browser_online');
    const handleOffline = () => emitPageDiagnostic('browser_offline');
    const handlePageHide = () => emitPageDiagnostic('page_hide', {}, { useBeacon: true });
    const handlePageShow = () => emitPageDiagnostic('page_show');
    const handleFreeze = () => emitPageDiagnostic('page_freeze', {}, { useBeacon: true });
    const handleResume = () => emitPageDiagnostic('page_resume');

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('freeze', handleFreeze);
    window.addEventListener('resume', handleResume);

    let longTaskObserver = null;
    try {
      if (typeof PerformanceObserver !== 'undefined') {
        longTaskObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration >= 1000) {
              emitPageDiagnostic('browser_long_task', {
                longTaskMs: Math.round(entry.duration)
              });
            }
          }
        });
        longTaskObserver.observe({ entryTypes: ['longtask'] });
      }
    } catch (_) {
      longTaskObserver = null;
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('freeze', handleFreeze);
      window.removeEventListener('resume', handleResume);
      if (longTaskObserver) {
        longTaskObserver.disconnect();
      }
    };
  }, [getFrontendDiagnosticContext]);

  // Define handleAnyEvent in the component scope
  const handleAnyEvent = useCallback((eventName, ...args) => {
    let actualPayload = args[0];
    let typeToValidate = eventName;

    // Check if the first argument itself looks like our wrapped event structure
    if (
        actualPayload &&
        typeof actualPayload === 'object' &&
        actualPayload.type === eventName && // Important: type in payload matches outer event name
        'payload' in actualPayload
    ) {
        // If so, the true payload is inside actualPayload.payload
        // And the type is already confirmed by actualPayload.type
        console.log(`SocketProvider: Received event [${eventName}] was already wrapped. Unwrapping for validation.`);
        actualPayload = actualPayload.payload;
        // typeToValidate remains eventName, as it's consistent.
    }

    // Skip validation for unknown event types (e.g. dynamic *_ack_* messages)
    if (!knownIncomingEventTypes.has(typeToValidate)) {
      console.warn(`SocketProvider: Unknown event type [${typeToValidate}] – skipping validation and emitting raw payload.`);
      debugEmitter.emit(typeToValidate, actualPayload);
      return; // Prevent further validation to avoid Zod runtime errors
    }

    // Check for missing critical fields specifically for commentary_update
    if (eventName === 'commentary_update' && actualPayload && typeof actualPayload === 'object') {
      const criticalFields = ['event_type', 'message_text', 'minute', 'formal_minute'];
      const missingFields = criticalFields.filter(f => actualPayload[f] === undefined || actualPayload[f] === null);
      if (missingFields.length > 0) {
        const identifier = `${actualPayload.event_type || 'UNKNOWN_TYPE'}@${actualPayload.formal_minute ?? actualPayload.minute ?? 'NA'}`;
        console.warn(`SocketProvider: [MISSING] commentary_update (${identifier}) is missing [${missingFields.join(', ')}]`, {
          receivedPayload: actualPayload
        });
      }
    }

    // No special wrapping needed – commentary_update now carries a single object.

    const eventToValidate = { type: typeToValidate, payload: actualPayload };

    console.log(`SocketProvider: Attempting to validate event type [${eventToValidate.type}] with payload:`, actualPayload);

    const validationResult = validateIncomingEvent(eventToValidate); // validateIncomingEvent uses eventToValidate.type to get schema

    if (validationResult.success) {
        // console.log(`SocketProvider: Event [${eventToValidate.type}] validated successfully. Emitting internally.`, validationResult.data); // data is the parsed payload
        // Ensure validationResult.data is what's expected by debugEmitter consumers for this event type
        debugEmitter.emit(eventToValidate.type, validationResult.data.payload); 
        console.log(`SocketProvider: Event [${eventToValidate.type}] validation SUCCESS. Emitted internally with:`, validationResult.data);
    } else {
      // IMPROVED ERROR HANDLING: For critical events like engine_state_update, emit anyway but log the issue
      console.error(
        `SocketProvider: Event [${eventToValidate.type}] FAILED validation.`,
        {
          typeBeingValidated: eventToValidate.type,
          attemptedPayload: actualPayload, // Log the payload that was attempted for this type
          rawReceivedArg0: args[0],    // Log the original first arg received by onAny
          errors: JSON.stringify(validationResult.error?.format ? validationResult.error.format() : validationResult.error, null, 2)
        }
      );
      
      // For critical real-time events, attempt recovery before falling back to raw data
      // This ensures the app continues working even if schema validation is too strict
      if (eventToValidate.type === 'engine_state_update' || eventToValidate.type === 'commentary_update') {
        // Task 22: Attempt event recovery for commentary events
        if (eventToValidate.type === 'commentary_update') {
          const recoveredEvent = attemptEventRecovery(eventToValidate, validationResult.error);
          if (recoveredEvent) {
            console.log(`SocketProvider: Successfully recovered ${eventToValidate.type} event after validation failure.`);
            debugEmitter.emit(eventToValidate.type, recoveredEvent.payload);
            return; // Use recovered event instead of raw data
          }
        }
        
        console.warn(`SocketProvider: Emitting raw ${eventToValidate.type} data despite validation failure to maintain functionality.`);
        debugEmitter.emit(eventToValidate.type, actualPayload);
      }
      // For other events, don't emit invalid data
    }
  }, []); // No dependencies, as validateIncomingEvent is a pure function from an import

  // Task 22: Event recovery function
  const attemptEventRecovery = useCallback((eventData, error) => {
    if (eventData.type === 'commentary_update' && eventData.payload) {
      const recovered = { ...eventData };
      const p = recovered.payload;

      // 1. Ensure event_type is present
      if (!p.event_type) {
        // Try fallback from a generic "type" field or default
        p.event_type = p.type || 'SYSTEM_MESSAGE';
      }

      // 2. Ensure minute is a valid number (default 0)
      if (p.minute === undefined || p.minute === null || Number.isNaN(p.minute)) {
        p.minute = 0;
      }

      // 3. Ensure formal_minute exists (use minute or "0")
      if (!p.formal_minute) {
        p.formal_minute = String(p.minute ?? 0);
      }

      // 4. Ensure message_text exists (try narrative.action or fallback message)
      if (!p.message_text) {
        if (p.narrative?.action) {
          p.message_text = p.narrative.action;
        } else if (p.event_type) {
          p.message_text = p.event_type.replace(/_/g, ' ').toLowerCase();
        } else {
          p.message_text = 'event';
        }
      }

      // 5. Timestamp defaults (ISO string) if missing
      if (!p.timestamp) {
        p.timestamp = new Date().toISOString();
      }

      // Validate the recovered event
      const recoveredValidation = validateIncomingEvent(recovered);
      return recoveredValidation.success ? recovered : null;
    }
    return null;
  }, []);

  // --- SENDER FUNCTIONS ---
  const sendEvent = useCallback((eventType, payload = {}, callback = null) => {
    console.log(`[SocketContext sendEvent] Called with eventType: '${eventType}', payload summary:`, getOutgoingSocketPayloadSummary(eventType, payload));
    console.log(`[SocketContext sendEvent] Socket status: ${statusRef.current}, exists: ${!!socketInstanceRef.current}`);
    
    if (!socketInstanceRef.current || statusRef.current !== 'connected') {
      console.error(`[SocketContext sendEvent] FAILED - Socket not connected. Status: ${statusRef.current}, exists: ${!!socketInstanceRef.current}`);
      return false;
    }

    const eventToSend = { type: eventType, payload };
    console.log(`[SocketContext sendEvent] About to validate event:`, getOutgoingSocketPayloadSummary(eventType, payload));

    const validationResult = validateOutgoingEvent(eventToSend);
    console.log(`[SocketContext sendEvent] Validation result:`, { 
      success: validationResult.success,
      hasError: !!validationResult.error,
      eventType: eventType
    });

    if (!validationResult.success) {
      const formattedError = validationResult.error?.format ? validationResult.error.format() : validationResult.error;
      console.error(
        `[SocketContext sendEvent] FAILED validation for '${eventType}'. Not sending. Event summary:`,
        getOutgoingSocketPayloadSummary(eventType, payload),
        'Errors:',
        JSON.stringify(formattedError, null, 2)
      );
      return false;
    }

    console.log(`[SocketContext sendEvent] SUCCESS - Validation passed. About to emit '${validationResult.data.type}' to socket...`);
    console.log(`[SocketContext sendEvent] Final payload summary being emitted:`, getOutgoingSocketPayloadSummary(validationResult.data.type, validationResult.data.payload));
    if (callback && typeof callback === 'function') {
      socketInstanceRef.current.emit(validationResult.data.type, validationResult.data.payload, callback);
    } else {
      socketInstanceRef.current.emit(validationResult.data.type, validationResult.data.payload);
    }
    console.log(`[SocketContext sendEvent] EVENT EMITTED to socket successfully: '${validationResult.data.type}'`);
    return true;
  }, []); // No deps – uses refs

  const sendControlSimulation = useCallback((action, value, ackCallback) => {
    console.log(`[SocketContext sendControlSimulation] Called with action: '${action}', value summary:`, getOutgoingSocketPayloadSummary('control_simulation', value || {}));
    const payload = value === undefined ? { action } : { action, ...value };
    const callback = typeof ackCallback === 'function'
      ? (response) => {
        console.log(`[SocketContext sendControlSimulation] Ack response summary for action '${action}':`, {
          responseType: response === null ? 'null' : typeof response,
          responseKeyCount: response && typeof response === 'object' ? Object.keys(response).length : 0,
          success: typeof response?.success === 'boolean' ? response.success : undefined,
          code: response?.code
        });
        ackCallback(response);
      }
      : null;
    const result = callback ? sendEvent('control_simulation', payload, callback) : sendEvent('control_simulation', payload);
    console.log(`[SocketContext sendControlSimulation] sendEvent returned:`, result);
    return result;
  }, [sendEvent]);

  const sendUserCommand = useCallback((commandPayload) => {
    return new Promise((resolve, reject) => {
      if (!socketInstanceRef.current || statusRef.current !== 'connected') {
        console.error(`SocketProvider: Cannot send user command. Socket not connected. Status: ${statusRef.current}`);
        reject(new Error('Socket not connected'));
        return;
      }

      const eventToSend = { type: 'send_engine_command', payload: commandPayload };
      const validationResult = validateOutgoingEvent(eventToSend);
      if (!validationResult.success) {
        const formattedError = validationResult.error?.format ? validationResult.error.format() : validationResult.error;
        console.error(
          `[SocketContext sendUserCommand] FAILED validation for 'send_engine_command'. Not sending. Event summary:`,
          getOutgoingSocketPayloadSummary('send_engine_command', commandPayload),
          'Errors:',
          JSON.stringify(formattedError, null, 2)
        );
        reject(new Error('send_engine_command validation failed'));
        return;
      }

      console.log(`[SocketContext sendUserCommand] Sending command summary:`, getOutgoingSocketPayloadSummary('send_engine_command', validationResult.data.payload));
      
      socketInstanceRef.current.emit('send_engine_command', validationResult.data.payload, (response) => {
        console.log(`[SocketContext sendUserCommand] Received response summary:`, {
          responseType: response === null ? 'null' : typeof response,
          responseKeyCount: response && typeof response === 'object' ? Object.keys(response).length : 0,
          success: typeof response?.success === 'boolean' ? response.success : undefined,
          code: response?.code
        });
        
        if (response && response.success) {
          resolve(response.data);
        } else {
          const error = new Error(response?.error || 'Unknown error from server');
          error.response = response;
          if (response?.code) {
            error.code = response.code;
          }
          reject(error);
        }
      });
    });
  }, []);

  const sendSaveGameState = useCallback((payload = {}) => {
    console.warn('[SocketContext sendSaveGameState] save_game_state is unsupported and was not sent.', {
      hasMatchId: Boolean(payload?.matchId)
    });
    return false;
  }, []);

  const sendLoadGameState = useCallback((payload = {}) => {
    return sendEvent('load_game_state', payload);
  }, []);

  const validateLeaveMatchPayload = useCallback((payload = {}) => {
    const eventToSend = { type: 'leave_match', payload };
    const validationResult = validateOutgoingEvent(eventToSend);
    if (!validationResult.success) {
      const formattedError = validationResult.error?.format ? validationResult.error.format() : validationResult.error;
      console.error(
        `[SocketContext sendLeaveMatch] FAILED validation for 'leave_match'. Not sending. Event:`,
        JSON.stringify(eventToSend, null, 2),
        'Errors:',
        JSON.stringify(formattedError, null, 2)
      );
      return { success: false, error: validationResult.error };
    }

    return {
      success: true,
      payload: validationResult.data.payload,
      matchId: String(validationResult.data.payload.matchId)
    };
  }, []);

  const flushPendingLeaveMatches = useCallback((socket = socketInstanceRef.current) => {
    if (!socket || statusRef.current !== 'connected' || pendingLeaveMatchIdsRef.current.size === 0) {
      return;
    }

    Array.from(pendingLeaveMatchIdsRef.current).forEach((matchId) => {
      const validationResult = validateLeaveMatchPayload({ matchId });
      if (!validationResult.success) {
        pendingLeaveMatchIdsRef.current.delete(matchId);
        return;
      }

      socket.emit('leave_match', validationResult.payload, (response) => {
        if (response?.success) {
          pendingLeaveMatchIdsRef.current.delete(matchId);
          return;
        }

        if (response?.error === 'Invalid leave_match request') {
          pendingLeaveMatchIdsRef.current.delete(matchId);
        }

        console.warn('[SocketProvider] Pending leave_match was not accepted:', response?.error || response);
      });
    });
  }, [validateLeaveMatchPayload]);

  const getNextMatchStreamClientSeq = useCallback(() => {
    const nextSeq = matchStreamClientSeqRef.current >= Number.MAX_SAFE_INTEGER
      ? 1
      : matchStreamClientSeqRef.current + 1;
    matchStreamClientSeqRef.current = nextSeq;
    return nextSeq;
  }, []);

  const sendJoinMatch = useCallback((payload = {}) => {
    return new Promise((resolve, reject) => {
      const joinPayload = {
        ...payload,
        clientStreamSeq: getNextMatchStreamClientSeq()
      };

      const sent = sendEvent('join_match', joinPayload, (response) => {
        if (response?.success) {
          const joinedMatchId = response?.matchId || joinPayload?.matchId;
          if (joinedMatchId) {
            pendingLeaveMatchIdsRef.current.delete(String(joinedMatchId));
          }
          resolve(response);
        } else {
          const error = new Error(response?.error || 'join_match failed');
          error.response = response;
          reject(error);
        }
      });

      if (!sent) {
        reject(new Error('join_match validation or connection failed'));
      }
    });
  }, [getNextMatchStreamClientSeq, sendEvent]);

  const sendLeaveMatch = useCallback((payload = {}) => {
    return new Promise((resolve, reject) => {
      const validationResult = validateLeaveMatchPayload(payload);
      if (!validationResult.success) {
        reject(new Error('leave_match validation failed'));
        return;
      }

      const { matchId, payload: validatedPayload } = validationResult;
      pendingLeaveMatchIdsRef.current.add(matchId);

      if (!socketInstanceRef.current || statusRef.current !== 'connected') {
        console.warn(`[SocketProvider] Socket is not connected. Queued leave_match for ${matchId}.`);
        resolve({ success: true, matchId, queued: true, left: false });
        return;
      }

      socketInstanceRef.current.emit('leave_match', validatedPayload, (response) => {
        if (response?.success) {
          pendingLeaveMatchIdsRef.current.delete(matchId);
          resolve(response);
        } else {
          if (response?.error === 'Invalid leave_match request') {
            pendingLeaveMatchIdsRef.current.delete(matchId);
          }
          const error = new Error(response?.error || 'leave_match failed');
          error.response = response;
          reject(error);
        }
      });
    });
  }, [validateLeaveMatchPayload]);

  const reconnect = useCallback((meta = {}) => {
    const socket = socketInstanceRef.current;

    if (!connectionKey) {
      console.warn('SocketProvider: Reconnect requested without an authenticated connection key.', meta);
      return false;
    }

    if (!session?.access_token) {
      console.warn('SocketProvider: Reconnect requested before an auth token is available.', meta);
      return false;
    }

    if (socket) {
      if (statusRef.current === 'connected' && socket.connected) {
        return true;
      }

      if (typeof socket.connect === 'function') {
        statusRef.current = 'connecting';
        setStatus('connecting');
        void (async () => {
          try {
            const latestToken = await getLatestAccessToken();
            if (
              socketInstanceRef.current !== socket ||
              currentConnectionKeyRef.current !== connectionKey
            ) {
              console.warn('SocketProvider: Reconnect aborted because the socket auth context changed.', meta);
              return;
            }
            if (!latestToken) {
              console.warn('SocketProvider: Reconnect aborted because no auth token was available.', meta);
              statusRef.current = 'error';
              setStatus('error');
              return;
            }
            updateSocketAuthToken(socket, latestToken);
            startSocketReconnection(socket);
            socket.connect();
          } catch (error) {
            console.warn('SocketProvider: Reconnect failed before socket.connect completed.', error);
            statusRef.current = 'error';
            setStatus('error');
          }
        })();
        return true;
      }

      console.warn('SocketProvider: Reconnect requested but socket.connect is unavailable.', meta);
      return false;
    }

    statusRef.current = 'connecting';
    setStatus('connecting');
    setReconnectNonce((value) => value + 1);
    return true;
  }, [connectionKey, getLatestAccessToken, session?.access_token]);

  // --- Effect for Managing Connection based on Session ---
  useEffect(() => {
    // Do not log `connectionKey` directly since it includes a token fragment.
    console.log('SocketProvider Effect: Running.', {
      hasConnectionKey: !!connectionKey,
      hasPreviousConnectionKey: !!lastConnectionKeyRef.current,
    });

    // Check if connection key actually changed
    if (connectionKey === lastConnectionKeyRef.current && socketInstanceRef.current) {
      console.log('SocketProvider Effect: Connection key unchanged and socket exists. Skipping reconnection.');
      return;
    }

    // Safely disconnect existing socket if connection key changed
    if (lastConnectionKeyRef.current !== connectionKey && socketInstanceRef.current) {
      console.log('SocketProvider Effect: Connection key changed. Disconnecting existing socket.');
      const existingSocket = socketInstanceRef.current;
      if (existingSocket && existingSocket.connected) {
        existingSocket.disconnect();
      } else if (existingSocket) {
        // If socket exists but not connected, just clean up references
        if (typeof existingSocket.close === 'function') {
          existingSocket.close();
        } else {
          existingSocket.disconnect?.();
        }
      }
      socketInstanceRef.current = null;
      setStatus('disconnected');
      setSocketId(null);
      setIsConnected(false);
    }

    let connectionTimeout = null; // Track the timeout for cleanup
    let effectActive = true;

    if (connectionKey) {
      if (!socketInstanceRef.current) {
        console.log('SocketProvider Effect: Valid connection key found and no socket exists. Connecting...');

        const connectSocket = async () => {
          let socketURL = import.meta.env.VITE_SOCKET_URL;
          if (!socketURL && process.env.NODE_ENV === 'test') {
            socketURL = 'http://localhost';
          }
          if (!socketURL) {
            console.error("SocketProvider: VITE_SOCKET_URL is not defined in .env file. Please set it (e.g., wss://yourdomain.com).");
            statusRef.current = 'error';
            setStatus('error');
            return;
          }

          const latestAccessToken = await getLatestAccessToken();
          if (!effectActive) {
            return;
          }

          if (!latestAccessToken) {
            console.warn(`SocketProvider: No access token available yet (retry ${authRetryCount}), delaying connection...`);
            statusRef.current = 'waiting_for_auth';
            setStatus('waiting_for_auth');
            // Retry connection after short delay in case token is being refreshed
            if (authRetryCount < 3) { // Limit retries to prevent infinite loops
              connectionTimeout = setTimeout(() => {
                console.log(`SocketProvider: Retrying connection after auth delay (attempt ${authRetryCount + 1})...`);
                setAuthRetryCount(prev => prev + 1); // This will trigger useEffect to re-run
              }, 1000);
            } else {
              console.error('SocketProvider: Max auth retry attempts reached. Token may be permanently unavailable.');
              statusRef.current = 'error';
              setStatus('error');
            }
            return;
          }

          // Reset retry count on successful auth
          if (authRetryCount > 0) {
            console.log('SocketProvider: Auth token available, resetting retry count.');
            setAuthRetryCount(0);
          }

          const clientInstanceId = clientInstanceIdRef.current || getStableSocketClientInstanceId();
          const newSocket = socketFactory(socketURL, {
            auth: { token: latestAccessToken, clientInstanceId },
            transports: ['websocket', 'polling'],
            timeout: SOCKET_CONNECT_TIMEOUT_MS,
            reconnection: true,
            reconnectionDelay: SOCKET_RECONNECTION_DELAY_MS,
            reconnectionDelayMax: SOCKET_RECONNECTION_DELAY_MAX_MS,
            reconnectionAttempts: SOCKET_RECONNECTION_ATTEMPTS,
            maxHttpBufferSize: 5 * 1024 * 1024, // 5 MB buffer size for large payloads
          });

          socketInstanceRef.current = newSocket;
          statusRef.current = 'connecting';
          setStatus('connecting');

          const markDisconnected = (nextStatus = 'disconnected') => {
            if (socketInstanceRef.current !== newSocket) {
              return;
            }
            statusRef.current = nextStatus;
            setStatus(nextStatus);
            setSocketId(null);
            setIsConnected(false);
          };

          const hardTeardown = (reason, nextStatus = 'disconnected') => {
            console.warn('SocketProvider: Hard teardown invoked. Reason:', reason);
            stopSocketReconnection(newSocket);
            try { newSocket.offAny(handleAnyEvent); } catch {}
            try {
              if (typeof newSocket.close === 'function') {
                newSocket.close();
              } else {
                newSocket.disconnect?.();
              }
            } catch {}
            if (socketInstanceRef.current === newSocket) {
              socketInstanceRef.current = null;
            }
            statusRef.current = nextStatus;
            setStatus(nextStatus);
            setSocketId(null);
            setIsConnected(false);
          };

          const isAuthConnectError = (error) => {
            const message = String(error?.message || '').toLowerCase();
            const code = String(error?.data?.code || error?.code || '').toLowerCase();
            return (
              shouldClearRejectedSocketAuth(getSocketAuthFailureCode(error)) ||
              message.includes('auth') ||
              message.includes('token') ||
              message.includes('unauthorized') ||
              message.includes('forbidden') ||
              code.includes('auth') ||
              code === 'unauthorized' ||
              code === 'forbidden'
            );
          };

          const handleSocketAuthFailure = async (error, source) => {
            const failureCode = getSocketAuthFailureCode(
              error,
              source === 'auth_failed' ? SOCKET_AUTH_FAILURE_CODES.TOKEN_EXPIRED : null
            );
            const repairKey = connectionKey || 'no-connection-key';

            if (socketAuthRepairRef.current.connectionKey !== repairKey) {
              socketAuthRepairRef.current = {
                connectionKey: repairKey,
                refreshAttempts: 0,
                userNotFoundAttempts: 0,
                inFlight: false
              };
            }

            const isMissingLocalUser = (
              failureCode === SOCKET_AUTH_FAILURE_CODES.USER_NOT_FOUND ||
              failureCode === SOCKET_AUTH_FAILURE_CODES.LOCAL_USER_NOT_FOUND
            );
            const missingUserAttempt = socketAuthRepairRef.current.userNotFoundAttempts;

            if (
              isMissingLocalUser &&
              missingUserAttempt < SOCKET_USER_NOT_FOUND_RETRY_DELAYS_MS.length
            ) {
              const retryDelay = SOCKET_USER_NOT_FOUND_RETRY_DELAYS_MS[missingUserAttempt];
              socketAuthRepairRef.current.userNotFoundAttempts += 1;
              hardTeardown(`${source}_${failureCode}_retry`, 'connecting');
              connectionTimeout = setTimeout(() => {
                if (
                  !effectActive ||
                  currentConnectionKeyRef.current !== connectionKey
                ) {
                  return;
                }
                setReconnectNonce((value) => value + 1);
              }, retryDelay);
              return;
            }

            if (socketAuthRepairRef.current.inFlight) {
              hardTeardown(`${source}_auth_repair_in_flight`, 'connecting');
              return;
            }

            const canRefresh = failureCode === SOCKET_AUTH_FAILURE_CODES.TOKEN_EXPIRED;
            if (canRefresh && socketAuthRepairRef.current.refreshAttempts < 1) {
              socketAuthRepairRef.current.inFlight = true;
              socketAuthRepairRef.current.refreshAttempts += 1;
              try {
                console.warn('SocketProvider: Socket token expired. Attempting one Supabase session refresh.');
                const { data, error: refreshError } = await supabase.auth.refreshSession();
                const refreshedSession = data?.session || null;
                const refreshedUserId = refreshedSession?.user?.id;

                if (refreshError || !refreshedSession?.access_token) {
                  throw refreshError || new Error('No refreshed Supabase session returned.');
                }

                if (
                  expectedSupabaseUserId &&
                  refreshedUserId &&
                  refreshedUserId !== expectedSupabaseUserId
                ) {
                  throw new Error('Refreshed Supabase session belongs to a different user.');
                }

                socketAuthOverrideTokenRef.current = refreshedSession.access_token;
                hardTeardown(`${source}_token_refreshed`, 'connecting');
                setReconnectNonce((value) => value + 1);
                return;
              } catch (refreshError) {
                console.warn('SocketProvider: Could not refresh expired socket token. Checking backend auth state.', refreshError);
                hardTeardown(`${source}_token_refresh_failed`, 'error');
                await clearRejectedSocketAuthStateIfConfirmed(
                  newSocket.auth?.token || latestAccessToken
                );
                return;
              } finally {
                socketAuthRepairRef.current.inFlight = false;
              }
            }

            hardTeardown(`${source}_${failureCode || 'auth'}_rejected`, 'error');
            if (shouldClearRejectedSocketAuth(failureCode)) {
              await clearRejectedSocketAuthStateIfConfirmed(
                newSocket.auth?.token || latestAccessToken
              );
            }
          };

          newSocket.on('connect', () => {
            console.log('SocketProvider: Connected!', newSocket.id);
            emitFrontendDiagnostic('socket_connect', getFrontendDiagnosticContext({
              socketId: newSocket.id
            }));
            socketAuthRepairRef.current = {
              connectionKey,
              refreshAttempts: 0,
              userNotFoundAttempts: 0,
              inFlight: false
            };
            socketAuthOverrideTokenRef.current = null;
            statusRef.current = 'connected';
            setStatus('connected');
            setSocketId(newSocket.id);
            setIsConnected(true);
            flushPendingLeaveMatches(newSocket);
          });

          newSocket.on('connect_error', (error) => {
            console.error('SocketProvider: Connection Error:', error?.message || error, error?.data || '');
            emitFrontendDiagnostic('socket_connect_error', getFrontendDiagnosticContext({
              socketId: newSocket.id || null,
              errorMessage: error?.message || String(error || 'unknown'),
              errorCode: error?.data?.code || error?.code || null
            }));
            if (isAuthConnectError(error)) {
              void handleSocketAuthFailure(error, 'connect_error');
              return;
            }
            markDisconnected('error');
          });

          newSocket.on('disconnect', (reason) => {
            console.log('SocketProvider: Disconnected!', reason);
            emitFrontendDiagnostic('socket_disconnect', getFrontendDiagnosticContext({
              socketId: newSocket.id || null,
              reason
            }), { useBeacon: true });
            markDisconnected('disconnected');
          });

          const socketManager = newSocket.io;
          if (socketManager && typeof socketManager.on === 'function') {
            socketManager.on('reconnect_attempt', (attempt) => {
              if (socketInstanceRef.current !== newSocket) {
                return;
              }
              console.log('SocketProvider: Reconnect attempt.', attempt);
              emitFrontendDiagnostic('socket_reconnect_attempt', getFrontendDiagnosticContext({
                socketId: newSocket.id || null,
                attempt
              }));
              statusRef.current = 'connecting';
              setStatus('connecting');
            });

            socketManager.on('reconnect', (attempt) => {
              if (socketInstanceRef.current !== newSocket) {
                return;
              }
              console.log('SocketProvider: Reconnected.', attempt);
              emitFrontendDiagnostic('socket_reconnect', getFrontendDiagnosticContext({
                socketId: newSocket.id || null,
                attempt
              }));
            });

            socketManager.on('reconnect_error', (error) => {
              if (socketInstanceRef.current !== newSocket) {
                return;
              }
              console.warn('SocketProvider: Reconnect error:', error?.message || error);
              emitFrontendDiagnostic('socket_reconnect_error', getFrontendDiagnosticContext({
                socketId: newSocket.id || null,
                errorMessage: error?.message || String(error || 'unknown'),
                errorCode: error?.data?.code || error?.code || null
              }));
            });

            socketManager.on('reconnect_failed', () => {
              if (socketInstanceRef.current !== newSocket) {
                return;
              }
              console.warn('SocketProvider: Reconnect attempts exhausted.');
              emitFrontendDiagnostic('socket_reconnect_failed', getFrontendDiagnosticContext({
                socketId: newSocket.id || null,
                attempts: SOCKET_RECONNECTION_ATTEMPTS
              }), { useBeacon: true });
              markDisconnected('error');
            });
          }

          // Custom server event for auth failure
          newSocket.on('auth_failed', (payload = {}) => {
            console.warn('SocketProvider: Received auth_failed from server.');
            const authFailedError = new Error('auth_failed');
            authFailedError.data = {
              code: payload?.code || payload?.reason || SOCKET_AUTH_FAILURE_CODES.TOKEN_EXPIRED
            };
            void handleSocketAuthFailure(authFailedError, 'auth_failed');
          });

          // Attach the generic event handler (must be after critical listeners)
          newSocket.onAny(handleAnyEvent);
        };

        if (process.env.NODE_ENV === 'test') {
          void connectSocket();
        } else {
          connectionTimeout = setTimeout(() => {
            void connectSocket();
          }, 250);
        }
         
         lastConnectionKeyRef.current = connectionKey; // Update the ref
      }
	    } else {
	      console.log('SocketProvider Effect: No valid connection key (user not authenticated), ensuring disconnection.');
	      if (socketInstanceRef.current) {
	        const existingSocket = socketInstanceRef.current;
	        if (existingSocket && existingSocket.connected) {
	          existingSocket.disconnect();
	        } else if (existingSocket) {
	          if (typeof existingSocket.close === 'function') {
	            existingSocket.close();
	          } else {
	            existingSocket.disconnect?.();
	          }
	        }
	        socketInstanceRef.current = null;
	      }
	      statusRef.current = 'disconnected';
	      setStatus('disconnected');
	      setSocketId(null);
	      setIsConnected(false);
	      lastConnectionKeyRef.current = null;
	    }

    // Cleanup function - safely handle socket cleanup
    return () => {
      console.log('SocketProvider Effect: Cleanup running.');
      effectActive = false;
      
      // Cancel the connection timeout if cleanup runs before timeout executes
      if (connectionTimeout) {
        console.log('SocketProvider Effect Cleanup: Canceling pending connection timeout.');
        clearTimeout(connectionTimeout);
      }
      
      // Clean up the actual socket instance from the ref
      if (socketInstanceRef.current) {
        console.log('SocketProvider Effect Cleanup: Detaching onAny handler and closing socket from ref.');
        try {
          socketInstanceRef.current.offAny(handleAnyEvent);
          if (socketInstanceRef.current.connected) {
            socketInstanceRef.current.disconnect();
          } else {
            if (typeof socketInstanceRef.current.close === 'function') {
              socketInstanceRef.current.close();
            } else {
              socketInstanceRef.current.disconnect?.();
            }
          }
          socketInstanceRef.current = null;
        } catch (error) {
          console.warn('SocketProvider Effect Cleanup: Error during socket cleanup:', error);
        }
      }
    };
  }, [connectionKey, socketFactory, authRetryCount, reconnectNonce, flushPendingLeaveMatches, getLatestAccessToken, expectedSupabaseUserId, getFrontendDiagnosticContext]);

  // Stable subscribe helper (never changes, uses debugEmitter under the hood)
  const subscribe = useCallback((eventName, handler) => {
    debugEmitter.on(eventName, handler);
    return () => debugEmitter.off(eventName, handler);
  }, []);

  const contextValue = useMemo(() => ({
    sendEvent,
    subscribe,
    // Snapshot value used to let consumers trigger effects when connection
    // status transitions (e.g. "disconnected" → "connected"). We keep the
    // getter as well for always-fresh reads, but the primitive `status` field
    // ensures React sees the change and re-runs any `useEffect` hooks that
    // list it as a dependency. Since connection status changes very rarely
    // (once per connect/disconnect), this still avoids re-renders on every
    // incoming packet while restoring expected behaviour.
    status,
    socketId,
    isConnected,
    get statusLive() {
      return statusRef.current; // optional alias if needed elsewhere
    },
    // Expose additional utilities that callers rely on (their references are
    // stable because the underlying functions are memoised with empty deps.)
    sendControlSimulation,
    sendUserCommand,
    sendSaveGameState,
    sendLoadGameState,
    sendJoinMatch,
    sendLeaveMatch,
    reconnect,
    emit: (eventName, payload, callback) => {
      if (!socketInstanceRef.current || statusRef.current !== 'connected') {
        console.error(`SocketProvider: Cannot emit event '${eventName}'. Socket not connected or doesn't exist. Status: ${statusRef.current}`);
        if (callback) callback({ success: false, error: 'Socket not connected' });
        return false;
      }
      console.log(`SocketProvider: Emitting direct event '${eventName}' with payload:`, payload);
      if (callback) {
        socketInstanceRef.current.emit(eventName, payload, callback);
      } else {
        socketInstanceRef.current.emit(eventName, payload);
      }
      return true;
    },
    // Expose raw socket and debug emitter for tests/debugging. Using getters so
    // that callers always receive the latest reference without forcing a new
    // context object.
    get rawSocket() {
      return socketInstanceRef.current;
    },
    unsubscribe: (eventName, handler) => debugEmitter.off(eventName, handler),
    _internalEmitterForDebug: debugEmitter,
    // Legacy alias so older tests using socketCtx.emit() still work
    emitLegacy: (eventType, payload, cb) => sendEvent(eventType, payload, cb),
  }), [sendEvent, subscribe, status, socketId, isConnected, sendControlSimulation, sendUserCommand, sendSaveGameState, sendLoadGameState, sendJoinMatch, sendLeaveMatch, reconnect]);

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
}; 
